import { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/apiClient";

export default function ImageUpload({ value, onChange, label = "Image" }) {
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Merci de choisir un fichier image.");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image trop volumineuse (max 5 Mo).");
            return;
        }
        const formData = new FormData();
        formData.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/admin/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Photo téléversée.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Erreur d'envoi");
        } finally {
            setUploading(false);
        }
    };

    const fullSrc = (() => {
        if (!value) return null;
        if (value.startsWith("http")) return value;
        if (value.startsWith("/api/")) {
            const base = process.env.REACT_APP_BACKEND_URL || "";
            return `${base}${value}`;
        }
        return value;
    })();

    return (
        <div className="space-y-2">
            <p className="text-xs uppercase tracking-widest text-[#5C3D2E]">{label}</p>
            <div className="flex items-start gap-3">
                {fullSrc ? (
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-[#2A1408] bg-white shrink-0">
                        <img
                            src={fullSrc}
                            alt="aperçu"
                            className="w-full h-full object-cover"
                        />
                        <button
                            type="button"
                            onClick={() => onChange("")}
                            className="absolute top-1 right-1 bg-[#2A1408] text-[#FFF1E0] rounded-full p-0.5"
                            aria-label="Retirer l'image"
                            data-testid="image-clear"
                        >
                            <X size={12} />
                        </button>
                    </div>
                ) : (
                    <div className="w-24 h-24 rounded-lg border-2 border-dashed border-[#B07B66] bg-[#FFF1E0] flex items-center justify-center text-[#B07B66] text-xs text-center px-2 shrink-0">
                        Aucune photo
                    </div>
                )}
                <div className="flex-1 space-y-2">
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        className="brand-btn-outline inline-flex items-center gap-2 text-sm py-2 px-4"
                        data-testid="image-upload-btn"
                    >
                        <Upload size={14} />{" "}
                        {uploading ? "Envoi…" : "Téléverser une photo"}
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid="image-upload-input"
                    />
                    <p className="text-xs text-[#5C3D2E]">
                        JPG, PNG ou WebP. Format carré (1:1) recommandé. Max 5 Mo.
                    </p>
                    <input
                        type="text"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="ou colle une URL d'image"
                        className="w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-xs"
                        data-testid="image-url-input"
                    />
                </div>
            </div>
        </div>
    );
}
